import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const raw = localStorage.getItem("cwk_user");
    return raw ? JSON.parse(raw) : null;
  });
  const [loading, setLoading] = useState(false);

  const persist = useCallback((token, u) => {
    localStorage.setItem("cwk_token", token);
    localStorage.setItem("cwk_user", JSON.stringify(u));
    setUser(u);
  }, []);

  const login = useCallback(async (email, password) => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      persist(data.access_token, data.user);
      return data.user;
    } finally {
      setLoading(false);
    }
  }, [persist]);

  const register = useCallback(async (payload) => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", payload);
      persist(data.access_token, data.user);
      return data.user;
    } finally {
      setLoading(false);
    }
  }, [persist]);

  const exchangeGoogleCode = useCallback(async (code, redirectUri, state) => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/google/exchange", {
        code, redirect_uri: redirectUri, state,
      });
      persist(data.access_token, data.user);
      return data.user;
    } finally {
      setLoading(false);
    }
  }, [persist]);

  const logout = useCallback(() => {
    localStorage.removeItem("cwk_token");
    localStorage.removeItem("cwk_user");
    setUser(null);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("cwk_token");
    if (token && !user) {
      api.get("/auth/me").then(({ data }) => {
        localStorage.setItem("cwk_user", JSON.stringify(data));
        setUser(data);
      }).catch(() => {
        localStorage.removeItem("cwk_token");
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, exchangeGoogleCode }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
};
